import { useEffect, useState } from "react";
import { ProcedureCodeFormRow } from "../billing-types";
import {
  DiagnosisKeys,
  DiagnosisPointer,
  DiagnosisPointersReturnType,
  MODIFIER_KEYS,
} from "./constant";

/**
 * @param values - values of the formik form
 * @param idx - index of the current diagnosis pointer
 * @param optionLimiter - limit of the options
 *
 * @returns options and handleChange function
 *
 * @author [Rajkumar Rathod]
 * @date [2025-09-24]
 */
export const useDiagnosisPointersOptionController = (
  values: ProcedureCodeFormRow[],
  idx: number,
  optionLimiter: number = 12
): DiagnosisPointersReturnType => {
  let selectedValues = ["", "", "", ""];
  const [options, setOptions] = useState<DiagnosisPointer>(
    getOptions(optionLimiter)
  );

  /* build options based on selected values filter out the values chosen by other field */
  const buildOptions = (selected: string[]): DiagnosisPointer => {
    // `Object.fromEntries` widens the keys back to `string`; the entries are
    // built from MODIFIER_KEYS, so the result covers every DiagnosisKeys.
    return Object.fromEntries(
      selected.map((_, idx) => {
        // for this pointer: allow everything except the values chosen by others
        const filtered = getLettersAtoL(optionLimiter).filter(
          (letter) => !selected.filter((_, i) => i !== idx).includes(letter)
        );
        return [MODIFIER_KEYS[idx], filtered];
      })
    ) as DiagnosisPointer;
  };

  /* trigger & set new options on every optionLimiter change */
  useEffect(() => {
    selectedValues?.forEach((el) => {
      if (el === "") setOptions(buildOptions(selectedValues));
    });
  }, [optionLimiter]);

  MODIFIER_KEYS.map((key, i) => {
    selectedValues[i] = values?.[idx]?.[key] ?? "";
  });

  // usage inside your handleChange
  const handleChange = (key: DiagnosisKeys, value: string | null) => {
    // update selectedValues
    const idx = MODIFIER_KEYS.indexOf(key);
    const newSelected = [...selectedValues];
    newSelected[idx] = value ?? "";

    selectedValues = newSelected;

    /* build new options with filtered new selected values */
    setOptions(buildOptions(newSelected));
  };

  return {
    selectedPointers: selectedValues,
    options,
    handleChange,
  };
};

const getLettersAtoL = (letterLimit: number) => {
  return Array.from({ length: letterLimit }, (_, i) =>
    String.fromCharCode(65 + i)
  );
};

const getOptions = (letterLimit: number): DiagnosisPointer => {
  return {
    diagnosisFirstPointers: getLettersAtoL(letterLimit),
    diagnosisSecondPointers: getLettersAtoL(letterLimit),
    diagnosisThirdPointers: getLettersAtoL(letterLimit),
    diagnosisFourthPointers: getLettersAtoL(letterLimit),
  };
};
